import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import {
  Container,
  TextField,
  CircularProgress,
  Typography,
  Button,
  Divider,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormControl,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import ClearIcon from "@mui/icons-material/Clear";
import { useDebounce, useViewport } from "../../helpers";
import IconButton from "@mui/material/IconButton";
import Autocomplete from "@mui/material/Autocomplete";
import { myDesignService } from "../../services";
import CheckIcon from "@mui/icons-material/Check";

const PREFIX = "DuplicateTarget";

const classes = {
  marginBottom: `${PREFIX}-marginBottom`,
  marginTop: `${PREFIX}-marginTop`,
  marginRight: `${PREFIX}-marginRight`,
  addTargetContainer: `${PREFIX}-addTargetContainer`,
  accordionDetailsContainer: `${PREFIX}-accordionDetailsContainer`,
  inputSize: `${PREFIX}-inputSize`,
  accordionDetailDisplayMod: `${PREFIX}-accordionDetailDisplayMod`,
  accordionDetailsMainContainer: `${PREFIX}-accordionDetailsMainContainer`,
  dividerColor: `${PREFIX}-dividerColor`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.marginBottom}`]: {
    marginBottom: "1rem",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.marginRight}`]: {
    marginRight: "1rem",
  },

  [`& .${classes.addTargetContainer}`]: {
    width: "100%",
    marginTop: "1rem",
    display: "flex",
    flexDirection: "column",
    maxWidth: "350px",
  },

  [`& .${classes.accordionDetailsContainer}`]: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  [`& .${classes.inputSize}`]: {
    width: "100%",
  },

  [`& .${classes.accordionDetailDisplayMod}`]: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  [`& .${classes.accordionDetailsMainContainer}`]: {
    width: "100%",
    display: "flex",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.dividerColor}`]: {
    backgroundColor: "rgba(211,175,95,0.6)",
  },
}));

const ControlledAutocomplete = ({
  options = [],
  renderInput,
  getOptionLabel,
  // onChange: ignored,
  control,
  defaultValue,
  name,
  renderOption,
  errors,
  handleTargetChange,
}) => {
  const { t } = useTranslation();
  return (
    <StyledDiv>
      <Controller
        render={({ onChange, ...props }) => (
          <Autocomplete
            options={options}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            renderInput={renderInput}
            onChange={(e, data) => {
              handleTargetChange(data, "timeZone");
              // onChange(data);
            }}
            {...props}
          />
        )}
        onChange={([, data]) => data}
        defaultValue={defaultValue}
        name={name}
        control={control}
        rules={{
          validate: (value) => (!value ? "timezoneError" : null),
        }}
      />
      {errors.hasOwnProperty(name) && (
        <FormHelperText
          id="timezoneRequired"
          error
          className={clsx(classes.negateMarginTop, classes.marginBottom)}
        >
          {t("homePage.myDesign.createForm.errors.timezone")}
        </FormHelperText>
      )}
    </StyledDiv>
  );
};

const DuplicateTarget = (props) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const {
    register,
    formState: { errors },
    trigger,
    control,
  } = useForm({
    mode: "onChange",
  });
  const [target, setTarget] = useState(null);
  const [newTargetName, setNewTargetName] = useState("");
  const [isNameSearching, setIsNameSearching] = useState(false);
  const debouncedSearchName = useDebounce(newTargetName, 1000);
  const [isNameUsed, setIsNameUsed] = useState(null);
  const [disabledNext, setDisableNext] = useState(true);

  useEffect(() => {
    setTarget({ ...props.selectedTarget });
  }, [props.selectedTarget]);

  function handleSaveNext() {
    props.handleSubmit(target, newTargetName, true);
  }

  function handleTargetChange(value, key) {
    let tmp = { ...target };
    tmp[key] = value;
    setTarget(tmp);
  }

  useEffect(() => {
    if (debouncedSearchName && debouncedSearchName.length > 0) {
      setIsNameSearching(true);
      myDesignService
        .checkTargetExist(props.frameId, debouncedSearchName)
        .then((results) => {
          setIsNameSearching(false);
          setIsNameUsed(results.exists);
          setDisableNext(results.exists);
        });
    } else {
      setDisableNext(true);
    }
  }, [debouncedSearchName]);

  if (!target) {
    return (
      <StyledDiv>
        <div className="loading-container">
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </StyledDiv>
    );
  } else {
    return (
      <Container>
        <StyledDiv>
          <Typography
            variant="h6"
            className={clsx(classes.marginBottom, classes.marginTop)}
          >
            {t("homePage.myDesign.createForm.duplicateTarget")}
          </Typography>
          <div className={classes.addTargetContainer}>
            <FormControl variant="outlined">
              <InputLabel htmlFor="addTargetName">
                {t("homePage.myDesign.createForm.newTargetName")}
              </InputLabel>
              <OutlinedInput
                name="addTargetName"
                label={t("homePage.myDesign.createForm.newTargetName")}
                value={newTargetName}
                onChange={(e) => setNewTargetName(e.target.value)}
                className={width > 1024 ? classes.marginRight : ""}
                variant={"outlined"}
                endAdornment={
                  (isNameUsed !== null || isNameSearching) && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="check if target name is available"
                        size="large"
                      >
                        {isNameSearching ? (
                          <CircularProgress />
                        ) : isNameUsed ? (
                          <ClearIcon className={classes.redIcon} />
                        ) : newTargetName.length > 0 ? (
                          <CheckIcon className={classes.greenIcon} />
                        ) : null}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              />
            </FormControl>
            {isNameUsed && (
              <FormHelperText
                id="targetNameUsed"
                error
                className={clsx(classes.negateMarginTop)}
              >
                {t("homePage.myDesign.createForm.targetNameUsed")}
              </FormHelperText>
            )}
          </div>
          <div className={classes.accordionDetailDisplayMod}>
            <div className={classes.accordionDetailsMainContainer}>
              <div className={classes.accordionDetailsContainer}>
                <div className={clsx(classes.inputSize, classes.marginBottom)}>
                  <ControlledAutocomplete
                    control={control}
                    errors={errors}
                    name={`${target.guid}-timezone`}
                    options={props.listTimeZones()}
                    getOptionLabel={(option) => `${option}`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("homePage.myDesign.createForm.choseTimezone")}
                        margin="normal"
                      />
                    )}
                    defaultValue={target.timeZone ? target.timeZone : null}
                    handleTargetChange={handleTargetChange}
                  />
                </div>
                <div className={classes.inputSize}>
                  <TextField
                    name={`target-redirectUrl-${target.guid}`}
                    label={t("homePage.myDesign.createForm.redirectUrl")}
                    defaultValue={target.targetUrl}
                    onChange={(e) =>
                      handleTargetChange(e.target.value, "targetUrl")
                    }
                    className={classes.marginRight}
                    fullWidth
                    variant={"outlined"}
                  />
                </div>
                <Divider
                  light
                  className={clsx(
                    classes.marginBottom,
                    classes.marginTop,
                    classes.dividerColor
                  )}
                />
                <Typography variant="h6" className={clsx(classes.marginBottom)}>
                  {t("homePage.myDesign.createForm.blinklOptions")}
                </Typography>
                <div className={clsx(classes.inputSize, classes.marginBottom)}>
                  <TextField
                    name={`campaign-id-${target.guid}`}
                    label={t("homePage.myDesign.createForm.campaignId")}
                    defaultValue={target.blinklCampaign}
                    onChange={(e) =>
                      handleTargetChange(e.target.value, "blinklCampaign")
                    }
                    className={classes.marginRight}
                    fullWidth
                    variant={"outlined"}
                  />
                </div>
                <div className={classes.inputSize}>
                  <TextField
                    name={`provider-id-${target.guid}`}
                    label={t("homePage.myDesign.createForm.providerId")}
                    defaultValue={target.blinklProvider}
                    onChange={(e) =>
                      handleTargetChange(e.target.value, "blinklProvider")
                    }
                    className={classes.marginRight}
                    fullWidth
                    variant={"outlined"}
                  />
                </div>
              </div>
            </div>
          </div>
          <Divider
            light
            className={clsx(classes.marginBottom, classes.marginTop)}
          />
          <div className={classes.stepperButtonPositions}>
            <Button
              color="primary"
              variant="contained"
              onClick={async () => {
                const result = await trigger();
                if (result) {
                  handleSaveNext();
                }
              }}
              className={clsx(classes.marginLeft)}
              disabled={
                disabledNext || props.loading || newTargetName.length <= 0
              }
            >
              {props.loading ? (
                <CircularProgress size={28} />
              ) : (
                t("homePage.myDesign.createForm.duplicate")
              )}
            </Button>
          </div>
        </StyledDiv>
      </Container>
    );
  }
};

export default DuplicateTarget;
